import React, { useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronDown, Check } from 'lucide-react';

import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';
import { useWeatherDigestAlerts, useExtremeWeatherAlerts } from '../../../utils/api-hooks/weatherDigest';
import { useMarketInsights } from '../../../utils/api-hooks/marketInsights';

// Tab constants
const DIGEST_TABS = {
  WEATHER: 'weather',
  EXTREME: 'extreme',
  INSIGHTS: 'insights',
};

/**
 * Get color classes based on market impact level
 * Supports both English (high/medium/low) and Spanish (alto/medio/bajo)
 */
const getImpactColors = (impact) => {
  const level = (impact || '').toLowerCase();

  // High impact - English: high, Spanish: alto
  if (level === 'high' || level === 'alto') {
    return {
      border: 'border-red-500',
      bg: 'bg-red-500',
      text: 'text-white',
    };
  }

  // Medium impact - English: medium, Spanish: medio
  if (level === 'medium' || level === 'medio') {
    return {
      border: 'border-orange-500',
      bg: 'bg-orange-500',
      text: 'text-white',
    };
  }

  // Low impact - English: low, Spanish: bajo
  if (level === 'low' || level === 'bajo') {
    return {
      border: 'border-bloom-green',
      bg: 'bg-bloom-green',
      text: 'text-white',
    };
  }

  // Default/unknown
  return {
    border: 'border-[#828382]',
    bg: 'bg-[#828382]',
    text: 'text-white',
  };
};

/**
 * Get color classes based on extreme weather alert type
 */
const getExtremeAlertColors = (alertType) => {
  const type = (alertType || '').toUpperCase();

  switch (type) {
    case 'FREEZE':
      return {
        border: 'border-blue-500',
        bg: 'bg-blue-500',
        text: 'text-white',
        bgLight: 'bg-blue-500/20',
        textColor: 'text-blue-500',
      };
    case 'EXTREME_HEAT':
      return {
        border: 'border-orange-600',
        bg: 'bg-orange-600',
        text: 'text-white',
        bgLight: 'bg-orange-600/20',
        textColor: 'text-orange-600',
      };
    case 'DROUGHT':
      return {
        border: 'border-amber-600',
        bg: 'bg-amber-600',
        text: 'text-white',
        bgLight: 'bg-amber-600/20',
        textColor: 'text-amber-600',
      };
    case 'FLOOD':
      return {
        border: 'border-cyan-600',
        bg: 'bg-cyan-600',
        text: 'text-white',
        bgLight: 'bg-cyan-600/20',
        textColor: 'text-cyan-600',
      };
    case 'STORM':
    case 'HURRICANE':
      return {
        border: 'border-purple-600',
        bg: 'bg-purple-600',
        text: 'text-white',
        bgLight: 'bg-purple-600/20',
        textColor: 'text-purple-600',
      };
    default:
      return {
        border: 'border-gray-500',
        bg: 'bg-gray-500',
        text: 'text-white',
        bgLight: 'bg-gray-500/20',
        textColor: 'text-gray-500',
      };
  }
};

/**
 * Format alert type for display (FREEZE -> Freeze, EXTREME_HEAT -> Extreme Heat)
 */
const formatAlertType = (type) => {
  if (!type) return '';
  return type
    .split('_')
    .map((word) =>
      word.toLowerCase() === 'fob'
        ? 'FOB'
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
};

/**
 * Get color classes based on market insight category
 */
const getInsightCategoryColors = (category) => {
  const cat = (category || '').toLowerCase();

  switch (cat) {
    case 'fob_price_alert':
    case 'fob_price':
      return {
        border: 'border-emerald-600',
        bg: 'bg-emerald-600',
        bgLight: 'bg-emerald-600/20',
        text: 'text-white',
        textColor: 'text-emerald-600',
      };
    case 'volume':
      return {
        border: 'border-blue-600',
        bg: 'bg-blue-600',
        bgLight: 'bg-blue-600/20',
        text: 'text-white',
        textColor: 'text-blue-600',
      };
    case 'market_share':
      return {
        border: 'border-purple-600',
        bg: 'bg-purple-600',
        bgLight: 'bg-purple-600/20',
        text: 'text-white',
        textColor: 'text-purple-600',
      };
    case 'transition':
      return {
        border: 'border-amber-600',
        bg: 'bg-amber-600',
        bgLight: 'bg-amber-600/20',
        text: 'text-white',
        textColor: 'text-amber-600',
      };
    default:
      return {
        border: 'border-gray-500',
        bg: 'bg-gray-500',
        bgLight: 'bg-gray-500/20',
        text: 'text-white',
        textColor: 'text-gray-500',
      };
  }
};

/**
 * Format insight category for display
 */
const formatInsightCategory = (category, t) => {
  const cat = (category || '').toLowerCase();
  switch (cat) {
    case 'fob_price_alert':
    case 'fob_price':
      return t('home.marketInsights.categories.fobPrice', 'FOB Price');
    case 'volume':
      return t('home.marketInsights.categories.volume', 'Volume');
    case 'market_share':
      return t('home.marketInsights.categories.marketShare', 'Market Share');
    case 'transition':
      return t('home.marketInsights.categories.transition', 'Transition');
    default:
      return toTitleCase(category);
  }
};

/**
 * Format the date display using iso_week and iso_year
 */
const formatAlertDate = (alert, t) => {
  if (alert.iso_week && alert.iso_year) {
    return t('home.weatherDigest.weekFormat', {
      week: alert.iso_week,
      year: alert.iso_year,
      defaultValue: `Week ${alert.iso_week}, ${alert.iso_year}`,
    });
  }
  return '';
};

/**
 * Format last updated date
 */
const formatLastUpdated = (dateStr, t) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    const formatted = date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
    return t('home.alertsDigest.lastUpdated', {
      date: formatted,
      defaultValue: `Last updated: ${formatted}`,
    });
  } catch {
    return null;
  }
};

/**
 * Convert string to title case
 */
const toTitleCase = (str) => {
  if (!str) return '';
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * CommoditiesAffectedBadges - Shows commodities with tooltip for overflow
 */
const CommoditiesAffectedBadges = ({ commodities, fontSize }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);

  const commoditiesArray = Array.isArray(commodities) ? commodities : [commodities];
  const visibleCommodities = commoditiesArray.slice(0, 3);
  const hiddenCommodities = commoditiesArray.slice(3);
  const hasMore = hiddenCommodities.length > 0;

  // Close tooltip when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <div className="flex flex-wrap gap-1">
      {visibleCommodities.map((commodity, idx) => (
        <span
          key={idx}
          className="px-2 py-1 rounded-full bg-bloom-green/20 text-bloom-green font-medium"
          style={{ fontSize: fontSize - 4 }}
        >
          {toTitleCase(commodity)}
        </span>
      ))}
      {hasMore && (
        <div className="relative" ref={tooltipRef}>
          <span
            className="px-2 py-1 rounded-full bg-[#828382]/20 text-[#828382] font-medium cursor-pointer hover:bg-[#828382]/30 transition-colors select-none"
            style={{ fontSize: fontSize - 4 }}
            onClick={() => setShowTooltip(!showTooltip)}
            onTouchEnd={(e) => {
              e.preventDefault();
              setShowTooltip(!showTooltip);
            }}
          >
            +{hiddenCommodities.length}
          </span>
          {showTooltip && (
            <div
              className="absolute top-full left-0 mt-1 p-2 rounded-lg bg-[#F0F5F2] dark:bg-[#3a3c3b] shadow-lg border border-[#C3C4C3] dark:border-[#828382] z-30 min-w-max max-w-[200px] max-h-[300px] overflow-y-auto"
            >
              <div className="flex flex-col items-start gap-1">
                {hiddenCommodities.map((commodity, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 rounded-full bg-bloom-green/20 text-bloom-green font-medium whitespace-nowrap"
                    style={{ fontSize: fontSize - 4 }}
                  >
                    {toTitleCase(commodity)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * TabToggle - Toggle between Weather and Extreme Weather tabs with underline style
 */
const TabToggle = ({ activeTab, onTabChange, fontSize }) => {
  const { t } = useTranslation();

  const tabs = [
    { id: DIGEST_TABS.WEATHER, label: t('home.weatherDigest.weatherTab', 'Weather') },
    { id: DIGEST_TABS.EXTREME, label: t('home.weatherDigest.extremeTab', 'Extreme Weather') },
    { id: DIGEST_TABS.INSIGHTS, label: t('home.marketInsights.tab', 'Historical Outlook') },
  ];

  return (
    <div className="flex gap-6">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`pb-2 font-medium transition-colors border-b-2 ${
            activeTab === tab.id
              ? 'text-bloom-green border-bloom-green'
              : 'text-[#828382] dark:text-[#5d625f] border-transparent hover:text-[#282A29] dark:hover:text-[#f0f5f2]'
          }`}
          style={{ fontSize: fontSize }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

/**
 * AlertCard - Weather alert card.
 * Layout: Date (left) | --- | Title | Commodity | Location | Summary | Footer (status + impact + last updated)
 */
const AlertCard = ({ alert, fontSize }) => {
  const { t } = useTranslation();
  const isActive = alert.is_active !== false;
  const impactColors = getImpactColors(alert.market_impact);
  const borderColor = !isActive ? 'border-[#9CA3AF]' : impactColors.border;
  const bgColor = !isActive
    ? 'bg-[#F3F4F6] dark:bg-[#1F1F1F]'
    : 'bg-[#F0F5F2] dark:bg-[#232121]';

  return (
    <div className={`flex-shrink-0 w-[260px] sm:w-[280px] border-l-4 ${borderColor} rounded-lg p-4 ${bgColor} flex flex-col gap-3`}>
      {/* Row 1: Date left */}
      <div className="flex items-center justify-between">
        <span className="text-[#828382] font-medium" style={{ fontSize: fontSize - 2 }}>
          {formatAlertDate(alert, t)}
        </span>
      </div>

      {/* Title */}
      <h4 className="font-semibold text-[#282A29] dark:text-[#ffffff] line-clamp-2" style={{ fontSize }}>
        {alert.title}
      </h4>

      {/* Commodity badges */}
      <CommoditiesAffectedBadges
        commodities={alert.commodities_affected || [alert.commodity]}
        fontSize={fontSize}
      />

      {/* Location */}
      <div className="text-[#5D625F] dark:text-[#a0a0a0]" style={{ fontSize: fontSize - 2 }}>
        {alert.region ? `${toTitleCase(alert.region)}, ` : ''}{toTitleCase(alert.country)}
      </div>

      {/* Summary */}
      <p className="text-[#5D625F] dark:text-[#828382] line-clamp-3 sm:line-clamp-4 lg:line-clamp-none flex-grow" style={{ fontSize: fontSize - 2 }}>
        {alert.impact_summary}
      </p>

      {/* Footer */}
      <div className="mt-auto space-y-2">
        {!isActive && alert.status_note && (
          <p className="text-[#828382] italic pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30" style={{ fontSize: fontSize - 3 }}>
            {alert.status_note}
          </p>
        )}

        {/* Status + Impact */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30">
          <div className="flex items-center gap-1">
            {isActive ? (
              <>
                <span className="w-2 h-2 rounded-full bg-bloom-green" />
                <span className="text-bloom-green font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                  {t('home.alertsDigest.active', 'Active')}
                </span>
              </>
            ) : (
              <>
                <span className="w-2 h-2 rounded-full border border-[#828382]" />
                <span className="text-[#828382] font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                  {t('home.alertsDigest.inactive', 'Inactive')}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#828382]" style={{ fontSize: fontSize - 2 }}>
              {t('home.weatherDigest.impact', 'Impact:')}
            </span>
            <span
              className={`px-2 py-0.5 rounded font-medium uppercase ${isActive ? `${impactColors.bg} ${impactColors.text}` : 'bg-[#9CA3AF] text-white'}`}
              style={{ fontSize: fontSize - 3 }}
            >
              {alert.market_impact || 'N/A'}
            </span>
          </div>
        </div>

        {/* Last updated */}
        {alert.last_evaluated_at && (
          <div className="text-[#828382] italic" style={{ fontSize: fontSize - 3 }}>
            {formatLastUpdated(alert.last_evaluated_at, t)}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ExtremeWeatherAlertCard - Extreme weather alert card.
 * Layout: Date (left) | Type badge (right) | Title | Commodities | Location | Summary | Footer (status + last updated)
 */
const ExtremeWeatherAlertCard = ({ alert, fontSize }) => {
  const { t } = useTranslation();
  const alertColors = getExtremeAlertColors(alert.specific_alert_type);
  const isActive = alert.status === 'Active';

  return (
    <div className={`flex-shrink-0 w-[260px] sm:w-[280px] border-l-4 ${alertColors.border} rounded-lg p-4 bg-[#F0F5F2] dark:bg-[#232121] flex flex-col gap-3`}>
      {/* Row 1: Date left, Alert type badge right */}
      <div className="flex items-center justify-between">
        <span className="text-[#828382] font-medium" style={{ fontSize: fontSize - 2 }}>
          {formatAlertDate(alert, t)}
        </span>
        <span
          className={`px-2 py-1 rounded-full ${alertColors.bgLight} ${alertColors.textColor} font-medium`}
          style={{ fontSize: fontSize - 3 }}
        >
          {formatAlertType(alert.specific_alert_type)}
        </span>
      </div>

      {/* Title (location) */}
      <h4 className="font-semibold text-[#282A29] dark:text-[#ffffff] line-clamp-2" style={{ fontSize }}>
        {alert.region ? `${toTitleCase(alert.region)}, ` : ''}{toTitleCase(alert.country)}
      </h4>

      {/* Commodities */}
      <CommoditiesAffectedBadges commodities={alert.commodities_affected} fontSize={fontSize} />

      {/* Summary */}
      <p className="text-[#5D625F] dark:text-[#828382] line-clamp-3 sm:line-clamp-4 lg:line-clamp-none flex-grow" style={{ fontSize: fontSize - 2 }}>
        {alert.impact_summary}
      </p>

      {/* Footer */}
      <div className="mt-auto space-y-2">
        <div className="flex items-center gap-1 pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30">
          {isActive ? (
            <>
              <span className="w-2 h-2 rounded-full bg-bloom-green" />
              <span className="text-bloom-green font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                {t('home.alertsDigest.active', 'Active')}
              </span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full border border-[#828382]" />
              <span className="text-[#828382] font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                {t('home.alertsDigest.inactive', 'Inactive')}
              </span>
            </>
          )}
        </div>

        {/* Last updated */}
        {alert.last_evaluated_at && (
          <div className="text-[#828382] italic" style={{ fontSize: fontSize - 3 }}>
            {formatLastUpdated(alert.last_evaluated_at, t)}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * MarketInsightCard - Market insight card.
 * Layout: Period (left) | Category badge (right) | Title | Commodities | Origin | Summary | Badges | Footer (status + last updated)
 */
const MarketInsightCard = ({ insight, fontSize }) => {
  const { t } = useTranslation();
  const categoryColors = getInsightCategoryColors(insight.insight_category);
  const isActive = new Date(insight.expires_at) > new Date();

  const periodDisplay = insight.period_name ||
    (insight.period_type === 'WEEK'
      ? `${t('home.marketInsights.week', 'Week')} ${insight.period_number}`
      : insight.period_type === 'TRANSITION'
        ? t('home.marketInsights.categories.transition', 'Transition')
        : `${t('home.marketInsights.month', 'Month')} ${insight.period_number}`);

  return (
    <div className={`flex-shrink-0 w-[260px] sm:w-[280px] border-l-4 ${isActive ? categoryColors.border : 'border-gray-400'} rounded-lg p-4 bg-[#F0F5F2] dark:bg-[#232121] flex flex-col gap-3`}>
      {/* Row 1: Period left, Category badge right */}
      <div className="flex items-center justify-between">
        <span className="text-[#828382] font-medium" style={{ fontSize: fontSize - 2 }}>
          {periodDisplay}, {insight.period_year}
        </span>
        <span
          className={`px-2 py-1 rounded-full ${categoryColors.bgLight} ${categoryColors.textColor} font-medium`}
          style={{ fontSize: fontSize - 3 }}
        >
          {formatInsightCategory(insight.insight_category, t)}
        </span>
      </div>

      {/* Title */}
      <h4 className="font-semibold text-[#282A29] dark:text-[#ffffff]" style={{ fontSize }}>
        {insight.title}
      </h4>

      {/* Commodities */}
      <CommoditiesAffectedBadges
        commodities={insight.commodities_affected || [insight.commodity]}
        fontSize={fontSize}
      />

      {/* Summary */}
      <p className="text-[#5D625F] dark:text-[#828382] line-clamp-3 sm:line-clamp-4 lg:line-clamp-none flex-grow" style={{ fontSize: fontSize - 2 }}>
        {insight.impact_summary}
      </p>

      {/* Peak/Trough badges */}
      <div className="flex flex-wrap gap-1">
        {insight.is_fob_peak && (
          <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-600 dark:text-red-400">
            {t('home.marketInsights.badges.fobPeak', 'FOB Peak')}
          </span>
        )}
        {insight.is_fob_trough && (
          <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-600 dark:text-green-400">
            {t('home.marketInsights.badges.fobLow', 'FOB Low')}
          </span>
        )}
        {insight.is_volume_peak && (
          <span className="px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-600 dark:text-blue-400">
            {t('home.marketInsights.badges.volPeak', 'Vol Peak')}
          </span>
        )}
        {insight.is_volume_trough && (
          <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-600 dark:text-purple-400">
            {t('home.marketInsights.badges.volLow', 'Vol Low')}
          </span>
        )}
        {insight.is_transition && (
          <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-600 dark:text-amber-400">
            {t('home.marketInsights.badges.transition', 'Transition')}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto space-y-2">
        <div className="flex items-center gap-1 pt-2 border-t border-[#C3C4C3]/30 dark:border-[#828382]/30">
          {isActive ? (
            <>
              <span className="w-2 h-2 rounded-full bg-bloom-green" />
              <span className="text-bloom-green font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                {t('home.alertsDigest.active', 'Active')}
              </span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full border border-[#828382]" />
              <span className="text-[#828382] font-medium uppercase" style={{ fontSize: fontSize - 3 }}>
                {t('home.marketInsights.expired', 'Expired')}
              </span>
            </>
          )}
        </div>

        {/* Last updated */}
        {insight.created_at && (
          <div className="text-[#828382] italic" style={{ fontSize: fontSize - 3 }}>
            {formatLastUpdated(insight.created_at, t)}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * StatusFilterDropdown - Dropdown to filter alerts by status (All, Active, Inactive)
 */
const StatusFilterDropdown = ({
  selectedStatus,
  onStatusChange,
  fontSize
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const statusOptions = [
    { value: 'all', label: t('home.alertsDigest.status.all', 'All') },
    { value: 'active', label: t('home.alertsDigest.status.active', 'Active') },
    { value: 'inactive', label: t('home.alertsDigest.status.inactive', 'Inactive') },
  ];

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedLabel = statusOptions.find(o => o.value === selectedStatus)?.label || 'All';

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F0F5F2] dark:bg-[#282a29] hover:bg-[#E8EBE9] dark:hover:bg-[#3a3c3b] text-[#5D625F] dark:text-[#a0a0a0] transition-colors"
        style={{ fontSize: fontSize - 2 }}
      >
        <span>{t('home.alertsDigest.statusLabel', 'Status:')} {selectedLabel}</span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-40 rounded-lg shadow-lg bg-white dark:bg-[#232121] border border-[#C3C4C3] dark:border-[#828382] z-20">
          <div className="py-1">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onStatusChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F5F2] dark:hover:bg-[#282a29] transition-colors text-[#5D625F] dark:text-[#a0a0a0] ${
                  selectedStatus === option.value ? 'bg-[#F0F5F2] dark:bg-[#282a29]' : ''
                }`}
                style={{ fontSize: fontSize - 2 }}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                  selectedStatus === option.value ? 'border-bloom-green bg-bloom-green' : 'border-bloom-green'
                }`}>
                  {selectedStatus === option.value && <Check size={14} className="text-white" />}
                </div>
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * CommodityFilterDropdown - Dropdown to filter alerts by commodity (multi-select)
 */
const CommodityFilterDropdown = ({
  commodities,
  selectedCommodities,
  onToggle,
  fontSize
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Check if all commodities are selected (empty array means all selected)
  const allSelected = selectedCommodities.length === 0 || selectedCommodities.length === commodities.length;

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (commodity) => {
    // If all are selected (empty array), start with all commodities then remove clicked one
    if (selectedCommodities.length === 0) {
      onToggle(commodities.filter((c) => c !== commodity));
      return;
    }

    const isSelected = selectedCommodities.includes(commodity);
    if (isSelected) {
      const newSelection = selectedCommodities.filter((c) => c !== commodity);
      // If only one left and we're removing it, set to empty (all selected)
      onToggle(newSelection.length === 0 ? [] : newSelection);
    } else {
      const newSelection = [...selectedCommodities, commodity];
      // If all commodities are now selected, set to empty (all selected)
      onToggle(newSelection.length === commodities.length ? [] : newSelection);
    }
  };

  const handleSelectAll = () => {
    onToggle([]); // Empty array means all selected
  };

  const handleClearAll = () => {
    // Clear all means no selection - but we need at least one, so show first commodity
    onToggle([commodities[0]]);
  };

  const getButtonText = () => {
    if (allSelected) {
      return t('home.weatherDigest.allCommodities', 'All Commodities');
    }
    return t('home.weatherDigest.selectedCount', '{{count}} selected', {
      count: selectedCommodities.length,
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F0F5F2] dark:bg-[#282a29] hover:bg-[#E8EBE9] dark:hover:bg-[#3a3c3b] text-[#5D625F] dark:text-[#a0a0a0] transition-colors"
        style={{ fontSize: fontSize - 2 }}
      >
        <span>{getButtonText()}</span>
        <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 rounded-lg shadow-lg bg-white dark:bg-[#232121] border border-[#C3C4C3] dark:border-[#828382] z-20">
          {/* Select All / Clear All buttons */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#C3C4C3] dark:border-[#828382]">
            <button
              onClick={handleSelectAll}
              className="text-bloom-green hover:underline"
              style={{ fontSize: fontSize - 2 }}
            >
              {t('home.weatherDigest.selectAll', 'Select All')}
            </button>
            <button
              onClick={handleClearAll}
              className="text-[#5D625F] dark:text-[#a0a0a0] hover:underline"
              style={{ fontSize: fontSize - 2 }}
            >
              {t('home.weatherDigest.clearAll', 'Clear All')}
            </button>
          </div>

          {/* Scrollable checkbox list */}
          <div className="max-h-48 overflow-y-auto py-1">
            {commodities.map((commodity) => {
              // When allSelected (empty array), all checkboxes should be marked
              const isSelected = allSelected || selectedCommodities.includes(commodity);
              return (
                <button
                  key={commodity}
                  onClick={() => handleToggle(commodity)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#F0F5F2] dark:hover:bg-[#282a29] transition-colors text-[#5D625F] dark:text-[#a0a0a0]"
                  style={{ fontSize: fontSize - 2 }}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'border-bloom-green bg-bloom-green' : 'border-bloom-green'
                  }`}>
                    {isSelected && <Check size={14} className="text-white" />}
                  </div>
                  <span>{toTitleCase(commodity)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * WeatherDigestAlerts - Main component with tabs and carousel of alert cards
 */
const WeatherDigestAlerts = ({ commodities = [] }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();
  const carouselRef = useRef(null);
  const [selectedCommodities, setSelectedCommodities] = useState([]);
  const [statusFilter, setStatusFilter] = useState('active');
  const [activeTab, setActiveTab] = useState(DIGEST_TABS.WEATHER);

  // Fetch weather alerts
  const {
    alerts: weatherAlerts,
    isLoading: isWeatherLoading,
    isError: isWeatherError
  } = useWeatherDigestAlerts(commodities);

  // Fetch extreme weather alerts
  const {
    alerts: extremeAlerts,
    isLoading: isExtremeLoading,
    isError: isExtremeError
  } = useExtremeWeatherAlerts(commodities);

  // Fetch market insights
  const {
    insights: marketInsights,
    isLoading: isInsightsLoading,
    isError: isInsightsError
  } = useMarketInsights(commodities);

  // Determine which data to show based on active tab
  const isLoading = activeTab === DIGEST_TABS.WEATHER
    ? isWeatherLoading
    : activeTab === DIGEST_TABS.EXTREME
      ? isExtremeLoading
      : isInsightsLoading;
  const isError = activeTab === DIGEST_TABS.WEATHER
    ? isWeatherError
    : activeTab === DIGEST_TABS.EXTREME
      ? isExtremeError
      : isInsightsError;
  const alerts = activeTab === DIGEST_TABS.WEATHER
    ? weatherAlerts
    : activeTab === DIGEST_TABS.EXTREME
      ? extremeAlerts
      : marketInsights;

  // Filter alerts by selected commodities and status
  const filteredAlerts = useMemo(() => {
    let result = alerts;

    // Filter by status
    if (statusFilter === 'active') {
      result = result.filter((alert) => {
        if (activeTab === DIGEST_TABS.WEATHER) {
          return alert.is_active !== false;
        }
        if (activeTab === DIGEST_TABS.EXTREME) {
          return alert.status === 'Active';
        }
        // Market insights: active if not expired
        return new Date(alert.expires_at) > new Date();
      });
    } else if (statusFilter === 'inactive') {
      result = result.filter((alert) => {
        if (activeTab === DIGEST_TABS.WEATHER) {
          return alert.is_active === false;
        }
        if (activeTab === DIGEST_TABS.EXTREME) {
          return alert.status !== 'Active';
        }
        // Market insights: inactive if expired
        return new Date(alert.expires_at) <= new Date();
      });
    }

    // Filter by commodities (empty array means all selected, no filtering needed)
    if (selectedCommodities.length > 0) {
      if (activeTab === DIGEST_TABS.WEATHER) {
        result = result.filter((alert) =>
          selectedCommodities.some(
            (c) => c.toLowerCase() === alert.commodity?.toLowerCase()
          )
        );
      } else {
        // For extreme weather and market insights, check commodities_affected array
        result = result.filter((alert) => {
          const affectedCommodities = Array.isArray(alert.commodities_affected)
            ? alert.commodities_affected
            : [alert.commodities_affected || alert.commodity];
          return selectedCommodities.some((c) =>
            affectedCommodities.some(
              (affected) => affected?.toLowerCase() === c.toLowerCase()
            )
          );
        });
      }
    }

    return result;
  }, [alerts, selectedCommodities, statusFilter, activeTab]);

  // Scroll carousel left/right
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({
        left: direction * 290, // card width + gap
        behavior: 'smooth',
      });
    }
  };

  // Get the empty state message based on active tab
  const getEmptyStateMessage = () => {
    if (activeTab === DIGEST_TABS.EXTREME) {
      return t('home.weatherDigest.noExtremeAlerts', 'No extreme weather alerts at this time');
    }
    if (activeTab === DIGEST_TABS.INSIGHTS) {
      return t('home.marketInsights.noInsights', 'No market insights at this time');
    }
    return t('home.weatherDigest.noAlerts', 'No weather alerts at this time');
  };

  // Empty state - no commodities selected
  if (commodities.length === 0) {
    return (
      <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg bg-white dark:bg-[#191919]">
        <div className="p-[16px] pb-0">
          <h3
            className="font-semibold text-bloom-green"
            style={{ fontSize: fontSize + 2 }}
          >
            {t('home.alertsDigest.title', 'Alerts digest')}
          </h3>
        </div>
        {/* Tab bar */}
        <div className="flex gap-6 px-[16px] pt-3 border-b border-[#C3C4C3]/30 dark:border-[#828382]/30">
          <button
            className="pb-2 border-b-2 border-bloom-green text-bloom-green font-medium"
            style={{ fontSize: fontSize }}
          >
            {t('home.weatherDigest.weatherTab', 'Weather')}
          </button>
          <button
            className="pb-2 border-b-2 border-transparent text-[#828382] font-medium"
            style={{ fontSize: fontSize }}
          >
            {t('home.weatherDigest.extremeTab', 'Extreme Weather')}
          </button>
          <button
            className="pb-2 border-b-2 border-transparent text-[#828382] font-medium"
            style={{ fontSize: fontSize }}
          >
            {t('home.marketInsights.tab', 'Historical Outlook')}
          </button>
        </div>
        <div className="p-[16px]">
          <div className="flex items-center justify-center min-h-[194px] bg-[#F0F5F2] dark:bg-[#232121] rounded-[4px] p-[16px]">
            <p
              className="font-semibold text-[#5D625F] dark:text-[#828382] text-center"
              style={{ fontSize }}
            >
              {t('home.weatherDigest.selectCommodities', 'Select your commodities to display weather alerts')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[#C3C4C3] dark:border-[#828382] rounded-lg bg-white dark:bg-[#191919]">
      {/* Header with title and filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-[16px] pb-0">
        <h3
          className="font-semibold text-bloom-green"
          style={{ fontSize: fontSize + 2 }}
        >
          {t('home.alertsDigest.title', 'Alerts digest')}
        </h3>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Status filter */}
          <StatusFilterDropdown
            selectedStatus={statusFilter}
            onStatusChange={setStatusFilter}
            fontSize={fontSize}
          />

          {/* Commodity filter */}
          {commodities.length >= 1 && (
            <CommodityFilterDropdown
              commodities={commodities}
              selectedCommodities={selectedCommodities}
              onToggle={setSelectedCommodities}
              fontSize={fontSize}
            />
          )}
        </div>
      </div>

      {/* Tabs under the title */}
      <div className="px-[16px] pt-3 border-b border-[#C3C4C3]/30 dark:border-[#828382]/30">
        <TabToggle
          activeTab={activeTab}
          onTabChange={setActiveTab}
          fontSize={fontSize}
        />
      </div>

      {/* Content area */}
      <div className="px-[16px] pb-[16px] pt-[16px]">
        {/* Loading state */}
        {isLoading && (
          <div
            className="flex gap-4 overflow-x-hidden px-2 py-2"
            role="status"
            aria-busy="true"
            aria-label={t('common.loading', 'Loading...')}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="skeleton-sheen h-[194px] w-[260px] shrink-0 rounded-lg sm:w-[280px]"
              />
            ))}
          </div>
        )}

        {/* Error state */}
        {isError && !isLoading && (
          <div className="flex items-center justify-center min-h-[194px] bg-[#F0F5F2] dark:bg-[#232121] rounded-[4px] p-[16px]">
            <p
              className="text-red-500 text-center"
              style={{ fontSize }}
            >
              {t('home.weatherDigest.loadError', 'Failed to load weather alerts. Please try again.')}
            </p>
          </div>
        )}

        {/* No alerts state */}
        {!isLoading && !isError && filteredAlerts.length === 0 && (
          <div className="flex items-center justify-center min-h-[194px] bg-[#F0F5F2] dark:bg-[#232121] rounded-[4px] p-[16px]">
            <p
              className="font-semibold text-[#5D625F] dark:text-[#828382] text-center"
              style={{ fontSize }}
            >
              {getEmptyStateMessage()}
            </p>
          </div>
        )}

        {/* Carousel with alerts */}
        {!isLoading && !isError && filteredAlerts.length > 0 && (
          <div className="relative">
            {/* Left arrow */}
            <button
              onClick={() => scrollCarousel(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-8 h-8 rounded-full bg-white dark:bg-[#282a29] shadow-md border border-[#C3C4C3] dark:border-[#828382] flex items-center justify-center text-[#5D625F] dark:text-[#a0a0a0] hover:bg-bloom-green hover:text-white hover:border-bloom-green transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} />
            </button>

            {/* Carousel container */}
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth px-2 py-2"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {filteredAlerts.map((alert, index) => (
                activeTab === DIGEST_TABS.WEATHER ? (
                  <AlertCard
                    key={`weather-${alert.commodity}-${alert.iso_week}-${alert.iso_year}-${index}`}
                    alert={alert}
                    fontSize={fontSize}
                  />
                ) : activeTab === DIGEST_TABS.EXTREME ? (
                  <ExtremeWeatherAlertCard
                    key={`extreme-${alert.country}-${alert.specific_alert_type}-${index}`}
                    alert={alert}
                    fontSize={fontSize}
                  />
                ) : (
                  <MarketInsightCard
                    key={`insight-${alert.unique_key || alert.insight_id || index}`}
                    insight={alert}
                    fontSize={fontSize}
                  />
                )
              ))}
            </div>

            {/* Right arrow */}
            <button
              onClick={() => scrollCarousel(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-8 h-8 rounded-full bg-white dark:bg-[#282a29] shadow-md border border-[#C3C4C3] dark:border-[#828382] flex items-center justify-center text-[#5D625F] dark:text-[#a0a0a0] hover:bg-bloom-green hover:text-white hover:border-bloom-green transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherDigestAlerts;
